<template>
  <div class="customers-table-wrapper w-100 mb-2">
    <b-table
      v-if="customers.list.length > 0 || customers.customersLoading"
      id="customers-table"
      ref="customers-table"
      data-testid="customers-table"
      :busy="customers.customersLoading"
      :sort-desc="sortValue"
      small1
      responsive
      borderless
      :items="customers.list"
      :fields="fields"
      class="i-table customers-table"
      selectable
      primary-key="id"
      @sort-changed="onSortChanged"
      @row-clicked="handleRowClick"
    >
      <template #cell(name)="data">
        <div class="d-flex">
          <div
            class="icon-wrapper d-flex align-items-center justify-content-center i-mr-8"
          >
            <i-icon
              symbol="small-user-single"
              :size="20"
              :stroke-width="1.25"
            ></i-icon>
          </div>
          <div class="d-flex flex-column customer-name">
            <router-link
              class="i-text-subheading-2 i-text-neutral-120"
              :to="{
                name: 'customer-overview',
                params: {
                  customer_id: data.item.id,
                },
              }"
              data-testid="customer-name-link"
              :aria-label="data.item.name"
              >{{ data.item.name }}</router-link
            >
            <small class="i-text-body-2 i-text-neutral-110">{{
              data.item.creator.email
            }}</small>
          </div>
        </div>
      </template>

      <template #cell(fraud_rating)="data">
        <CustomerFraudRating
          :fraud-rating="data.item.fraud_rating"
          :customer-id="data.item.id"
        />
      </template>

      <template #cell(status)="data">
        <CustomerApprovalStatus :approval-status="data.item.approved" />
      </template>

      <template #cell(docs)="data">
        <div class="d-flex flow-row align-items-center">
          <i-tooltip
            :target="`#total-id-${data.item.id}`"
            title="Total Documents"
            :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
          ></i-tooltip>
          <div :id="`total-id-${data.item.id}`" class="icon-container">
            <i-icon
              symbol="small-file"
              :size="16"
              :colour="palette['light-neutral-90']"
            ></i-icon>
          </div>
          <span class="i-ml-2 i-mr-6">{{ data.item.no_of_documents }}</span>

          <i-tooltip
            :target="`#fraudulent-id-${data.item.id}`"
            title="Fraudulent Documents"
            :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
          ></i-tooltip>
          <div :id="`fraudulent-id-${data.item.id}`" class="icon-container">
            <i-icon
              symbol="small-file-alert"
              :size="16"
              :colour="
                data.item.no_of_fraudulent_documents > 0
                  ? palette['light-error-90']
                  : palette['light-neutral-90']
              "
            ></i-icon>
          </div>
          <span class="i-ml-2">{{ data.item.no_of_fraudulent_documents }}</span>
        </div>
      </template>

      <template #cell(created_at)="data">
        {{ formatDateTime(data.item.created_at, 'lll') }}
      </template>

      <template #table-busy>
        <i-spinner
          data-testid="customers-table-loading"
          class="mx-auto i-my-16"
          size="xl"
        ></i-spinner>
      </template>
    </b-table>

    <div
      v-else-if="!customers.customersLoading"
      data-testid="customers-table-empty-state"
      class="empty-state d-flex flex-column justify-content-center align-items-center"
    >
      <empty-page
        icon="large-user-multiple"
        :title="emptyStateTitle"
        :message="emptyStateMessage"
        btn-text="New Customer"
        :btn-disabled="isUploadAccessBlocked"
        :btn-click="createNewCustomer"
      ></empty-page>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  iIcon,
  iBadgeColours,
  iTooltip,
  iTooltipAlignmentMode,
  iSpinner,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import EmptyPage from '@/shared/components/EmptyPage';
import CustomerFraudRating from '@/V2/customers/CustomerFraudRating.vue';
import CustomerApprovalStatus from '@/V2/customers/CustomerApprovalStatus.vue';
import { getAndSyncQueryParams, updateQueryParams } from '@/utils/queryParams';
import { formatDateTime } from '@/utils/date';

export default {
  components: {
    iIcon,
    iSpinner,
    iTooltip,
    EmptyPage,
    CustomerFraudRating,
    CustomerApprovalStatus,
  },
  data() {
    return {
      iBadgeColours,
      iTooltipAlignmentMode,
      palette,
      fields: [
        {
          key: 'name',
          label: 'Customer',
          tdClass: 'customer-column',
        },
        {
          key: 'fraud_rating',
          label: 'Fraud Rating',
          thClass: 'text-left',
          tdClass: 'text-left fraud-rating-column',
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'text-left',
          tdClass: 'text-left status-column',
          class: 'd-none d-md-table-cell',
        },
        {
          key: 'docs',
          label: 'Documents',
          thClass: 'text-left',
          tdClass: 'text-left docs-column',
          class: 'd-none d-md-table-cell',
        },
        {
          key: 'created_at',
          label: 'Created',
          thClass: 'text-left',
          tdClass: 'customer-created-at text-left',
          sortable: true,
        },
      ],
    };
  },
  computed: {
    ...mapState(['customers']),
    ...mapGetters({
      isUploadAccessBlocked: 'isUploadAccessBlocked',
    }),

    hasFiltersApplied() {
      return this.params && Object.keys(this.params).length > 0;
    },

    emptyStateTitle() {
      return this.hasFiltersApplied
        ? 'No customers match these filters'
        : "You haven't created any customers yet";
    },
    emptyStateMessage() {
      return this.hasFiltersApplied
        ? 'Matching customers will appear here'
        : 'All of your customers will appear here. Start by creating a new customer';
    },
    sortValue() {
      return this.params && this.params.order === '-created_at';
    },
    isFromAnalytics() {
      return this.$route.query.return_to === 'analytics';
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
      immediate: true,
    },
  },
  methods: {
    formatDateTime,
    handleRowClick(customer) {
      const routeData = {
        name: 'customer-overview',
        params: {
          customer_id: customer.id,
        },
      };

      // if the user is coming from analytics, we need to preserve the query params
      if (this.isFromAnalytics) {
        routeData.query = this.$route.query;
      }

      this.$router.push(routeData);
    },
    createNewCustomer() {
      this.$router.push({
        name: 'new-customer',
      });
    },
    onSortChanged(value) {
      const sortValue = value.sortDesc ? '-created_at' : 'created_at';
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          order: sortValue,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.customers-table-wrapper {
  .empty-state {
    img {
      max-width: 200px;
    }
  }
  .customers-table {
    color: $light-neutral-120;

    table {
      th {
        vertical-align: bottom !important;
      }
    }

    .icon-wrapper {
      background: $light-primary-30;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      flex-shrink: 0;
      .i-icon {
        color: $light-primary-110 !important;
      }
    }
    .customer-column {
      width: 50%;
      .customer-name {
        a {
          max-width: 60ch;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }

    .customer-created-at {
      white-space: nowrap;
      width: 160px;
    }

    .status-column,
    .docs-column {
      width: 135px;
    }

    .fraud-rating-column {
      width: 170px;
    }

    .status-column .i-badge,
    .fraud-rating-column .i-badge {
      display: inline-flex !important;
      white-space: nowrap;
    }
  }
}
</style>
